import {
  BRAND_HANDLE,
  BRAND_OG_IMAGE_URL,
  BRAND_PERSON_NAME,
  BRAND_SITE_URL,
  BRAND_SUPPORT_EMAIL,
  getBrandSiteHostname,
} from "@/lib/brandConfig";

import { useSEO } from "@/hooks/useSEO";
import { Scale } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const TerminosYCondiciones = () => {
  const navigate = useNavigate();
  const [canGoBack, setCanGoBack] = useState(false);

  const { t, i18n } = useTranslation();

  const brandHost = getBrandSiteHostname();
  const canonicalBase = BRAND_SITE_URL || `https://www.${brandHost}`;

  // Detectar si el usuario llegó desde otra página del sitio
  useEffect(() => {
    // Si hay un state con 'from' o si hay historial previo, puede volver
    const hasHistory = window.history.length > 1;
    setCanGoBack(hasHistory);
  }, []);

  const handleBack = () => {
    if (canGoBack) {
      // Volver a la página anterior
      navigate(-1);
    } else {
      // Si no hay historial, ir al inicio
      navigate("/");
    }
  };

  useSEO({
    title: t("termsAndConditions.seo.title"),
    description: t("termsAndConditions.seo.description"),
    keywords: t("termsAndConditions.seo.keywords"),
    ogImage:
      BRAND_OG_IMAGE_URL,
    ogTitle: t("termsAndConditions.seo.ogTitle"),
    ogDescription: t("termsAndConditions.seo.ogDescription"),
    canonical: `${canonicalBase}/terminos-y-condiciones`,
    author: BRAND_PERSON_NAME,
    publishedTime: "2026-02-19T00:00:00.000Z",
    modifiedTime: "2026-03-02T00:00:00.000Z",
    type: "article",
    locale: i18n.language === "es" ? "es_AR" : i18n.language === "pt" ? "pt_BR" : i18n.language === "de" ? "de_DE" : i18n.language === "fr" ? "fr_FR" : "en_US",
    robots: "index, follow",
  });

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <main className="max-w-4xl mx-auto px-6 py-16 md:py-20">
        <header className="text-center mb-16 animate-fade-in">
          <div className="flex justify-end mb-4">
            <LanguageSwitcher variant="dark" />
          </div>

          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/5 border border-white/10 mb-6">
            <Scale className="w-8 h-8 text-white/80" strokeWidth={1.5} />
          </div>

          <h1 className="text-4xl md:text-5xl font-display font-medium text-white tracking-wide mb-4">
            {t("termsAndConditions.header.title")}
          </h1>

          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="w-12 h-px bg-white/20" />
            <p className="text-xs uppercase tracking-[0.25em] text-white/60 font-medium">
              {brandHost}
            </p>
            <span className="w-12 h-px bg-white/20" />
          </div>

          <p className="text-sm text-white/50 italic">
            {t("termsAndConditions.header.lastUpdated")}
          </p>
        </header>

        <div className="prose prose-invert prose-lg max-w-none">
          <section
            className="mb-12 animate-fade-in"
            style={{ animationDelay: "0.1s" }}
          >
            <h2 className="text-2xl font-semibold text-white mb-6 pb-3 border-b border-white/10">
              {t("termsAndConditions.section1.title")}
            </h2>
            <div className="space-y-4 text-white/80 leading-relaxed">
              <p>
                {t("termsAndConditions.section1.content.p1")}
              </p>
              <p>
                {t("termsAndConditions.section1.content.p2")}
              </p>
            </div>
          </section>

          <section
            className="mb-12 animate-fade-in"
            style={{ animationDelay: "0.15s" }}
          >
            <h2 className="text-2xl font-semibold text-white mb-6 pb-3 border-b border-white/10">
              {t("termsAndConditions.section2.title")}
            </h2>
            <div className="space-y-4 text-white/80 leading-relaxed">
              <p>
                {t("termsAndConditions.section2.content.p1")}
              </p>
              <p>
                {t("termsAndConditions.section2.content.p2")}
              </p>
              <p>
                {t("termsAndConditions.section2.content.p3")}
              </p>
            </div>
          </section>

          <section
            className="mb-12 animate-fade-in"
            style={{ animationDelay: "0.2s" }}
          >
            <h2 className="text-2xl font-semibold text-white mb-6 pb-3 border-b border-white/10">
              {t("termsAndConditions.section3.title")}
            </h2>

            <h3 className="text-xl font-medium text-white/90 mb-4 mt-6">
              {t("termsAndConditions.section3.subsection1.title")}
            </h3>
            <div className="space-y-4 text-white/80 leading-relaxed mb-6">
              <p>
                {t("termsAndConditions.section3.subsection1.content.p1")}
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                {(t("termsAndConditions.section3.subsection1.content.items", { returnObjects: true }) as string[]).map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>

            <h3 className="text-xl font-medium text-white/90 mb-4">
              {t("termsAndConditions.section3.subsection2.title")}
            </h3>
            <div className="space-y-4 text-white/80 leading-relaxed">
              <p>
                {t("termsAndConditions.section3.subsection2.content.p1")}
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                {(t("termsAndConditions.section3.subsection2.content.items", { returnObjects: true }) as string[]).map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>
          </section>

          <section
            className="mb-12 animate-fade-in"
            style={{ animationDelay: "0.25s" }}
          >
            <h2 className="text-2xl font-semibold text-white mb-6 pb-3 border-b border-white/10">
              {t("termsAndConditions.section4.title")}
            </h2>

            <h3 className="text-xl font-medium text-white/90 mb-4 mt-6">
              {t("termsAndConditions.section4.subsection1.title")}
            </h3>
            <div className="space-y-4 text-white/80 leading-relaxed mb-6">
              <p>
                {t("termsAndConditions.section4.subsection1.content.p1")}
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                {(t("termsAndConditions.section4.subsection1.content.items", { returnObjects: true }) as string[]).map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
              <p>
                {t("termsAndConditions.section4.subsection1.content.p2")}
              </p>
            </div>

            <h3 className="text-xl font-medium text-white/90 mb-4">
              {t("termsAndConditions.section4.subsection2.title")}
            </h3>
            <div className="space-y-4 text-white/80 leading-relaxed">
              <p>
                {t("termsAndConditions.section4.subsection2.content.p1")}
              </p>
              <p>
                {t("termsAndConditions.section4.subsection2.content.p2")}
              </p>
            </div>
          </section>

          <section
            className="mb-12 animate-fade-in"
            style={{ animationDelay: "0.3s" }}
          >
            <h2 className="text-2xl font-semibold text-white mb-6 pb-3 border-b border-white/10">
              {t("termsAndConditions.section5.title")}
            </h2>
            <div className="space-y-4 text-white/80 leading-relaxed">
              <p>
                {t("termsAndConditions.section5.content.p1")}
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                {(t("termsAndConditions.section5.content.items", { returnObjects: true }) as string[]).map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
              <p>
                {t("termsAndConditions.section5.content.p2")}
              </p>
            </div>
          </section>

          <section
            className="mb-12 animate-fade-in"
            style={{ animationDelay: "0.35s" }}
          >
            <h2 className="text-2xl font-semibold text-white mb-6 pb-3 border-b border-white/10">
              {t("termsAndConditions.section6.title")}
            </h2>
            <div className="space-y-4 text-white/80 leading-relaxed">
              <p>
                {t("termsAndConditions.section6.content.p1")}
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                {(t("termsAndConditions.section6.content.items", { returnObjects: true }) as Array<{label: string, text: string}>).map((item, idx) => (
                  <li key={idx}>
                    <strong className="text-white">{item.label}</strong> {item.text}
                  </li>
                ))}
              </ul>
              <p>
                {t("termsAndConditions.section6.content.p2")}
              </p>
            </div>
          </section>

          <section
            className="mb-12 animate-fade-in"
            style={{ animationDelay: "0.4s" }}
          >
            <h2 className="text-2xl font-semibold text-white mb-6 pb-3 border-b border-white/10">
              {t("termsAndConditions.section7.title")}
            </h2>
            <div className="space-y-4 text-white/80 leading-relaxed">
              <p>
                {t("termsAndConditions.section7.content.p1")}
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  Utilizar nuestro{" "}
                  <a
                    href="/dar-de-baja"
                    className="text-white/90 hover:text-white underline font-medium"
                  >
                    {t("termsAndConditions.navigation.onlineForm")}
                  </a>
                </li>
                <li>
                  Enviar un correo electrónico a{" "}
                  <a
                    href={`mailto:${BRAND_SUPPORT_EMAIL}`}
                    className="text-white/90 hover:text-white underline"
                  >
                    {BRAND_SUPPORT_EMAIL}
                  </a>
                </li>
                <li>
                  {i18n.language === "es" ? "Comunicarse directamente con el procesador de pago correspondiente" : 
                   i18n.language === "pt" ? "Entrar em contato diretamente com o processador de pagamento correspondente" :
                   i18n.language === "de" ? "Sich direkt an den entsprechenden Zahlungsabwickler wenden" :
                   i18n.language === "fr" ? "Contacter directement le processeur de paiement correspondant" :
                   "Contact the corresponding payment processor directly"}
                </li>
              </ul>
              <p>
                {t("termsAndConditions.section7.content.p2")}
              </p>
              <p>
                {t("termsAndConditions.section7.content.p3")}
              </p>
            </div>

            <h3 className="text-xl font-medium text-white/90 mb-4 mt-6">
              {t("termsAndConditions.section7.subsection1.title")}
            </h3>
            <div className="space-y-4 text-white/80 leading-relaxed">
              <p dangerouslySetInnerHTML={{ __html: t("termsAndConditions.section7.subsection1.content.p1") }} />
              <p dangerouslySetInnerHTML={{ __html: t("termsAndConditions.section7.subsection1.content.p2") }} />
              <ul className="list-disc list-inside space-y-2 ml-4">
                {(t("termsAndConditions.section7.subsection1.content.items", { returnObjects: true }) as string[]).map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
              <p dangerouslySetInnerHTML={{ __html: t("termsAndConditions.section7.subsection1.content.p3") }} />
              <ul className="list-disc list-inside space-y-2 ml-4">
                {(t("termsAndConditions.section7.subsection1.content.violations", { returnObjects: true }) as Array<{label: string, text: string}>).map((item, idx) => (
                  <li key={idx}>
                    <strong className="text-white">{item.label}</strong> {item.text}
                  </li>
                ))}
              </ul>
              <p>
                {t("termsAndConditions.section7.subsection1.content.p4")}
              </p>
            </div>
          </section>

          <section
            className="mb-12 animate-fade-in"
            style={{ animationDelay: "0.45s" }}
          >
            <h2 className="text-2xl font-semibold text-white mb-6 pb-3 border-b border-white/10">
              {t("termsAndConditions.section8.title")}
            </h2>
            <div className="space-y-4 text-white/80 leading-relaxed">
              <p>
                {t("termsAndConditions.section8.content.p1")}
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                {(t("termsAndConditions.section8.content.items", { returnObjects: true }) as string[]).map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
              <p>
                {t("termsAndConditions.section8.content.p2")}
              </p>
            </div>

            <h3 className="text-xl font-medium text-white/90 mb-4 mt-6">
              {t("termsAndConditions.section8.subsection1.title")}
            </h3>
            <div className="space-y-4 text-white/80 leading-relaxed">
              <p>
                {t("termsAndConditions.section8.subsection1.content.p1")}
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                {(t("termsAndConditions.section8.subsection1.content.items", { returnObjects: true }) as string[]).map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
              <p>
                {t("termsAndConditions.section8.subsection1.content.p2")}
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                {(t("termsAndConditions.section8.subsection1.content.violations", { returnObjects: true }) as Array<{label: string, text: string}>).map((item, idx) => (
                  <li key={idx}>
                    <strong className="text-white">{item.label}</strong> {item.text}
                  </li>
                ))}
              </ul>
              <p>
                {t("termsAndConditions.section8.subsection1.content.p3")}
              </p>
            </div>
          </section>

          <section
            className="mb-12 animate-fade-in"
            style={{ animationDelay: "0.5s" }}
          >
            <h2 className="text-2xl font-semibold text-white mb-6 pb-3 border-b border-white/10">
              {t("termsAndConditions.section9.title")}
            </h2>
            <div className="space-y-4 text-white/80 leading-relaxed">
              <p>
                {t("termsAndConditions.section9.content.p1")}
              </p>
            </div>
          </section>

          <section
            className="mb-12 animate-fade-in"
            style={{ animationDelay: "0.55s" }}
          >
            <h2 className="text-2xl font-semibold text-white mb-6 pb-3 border-b border-white/10">
              {t("termsAndConditions.section10.title")}
            </h2>
            <div className="space-y-4 text-white/80 leading-relaxed">
              <p>
                {t("termsAndConditions.section10.content.p1")}
              </p>
            </div>
          </section>

          <section
            className="mb-12 animate-fade-in"
            style={{ animationDelay: "0.6s" }}
          >
            <h2 className="text-2xl font-semibold text-white mb-6 pb-3 border-b border-white/10">
              {t("termsAndConditions.section11.title")}
            </h2>
            <div className="space-y-4 text-white/80 leading-relaxed">
              <p dangerouslySetInnerHTML={{ __html: t("termsAndConditions.section11.content.p1") }} />
              <p dangerouslySetInnerHTML={{ __html: t("termsAndConditions.section11.content.p2") }} />
              <p>
                {t("termsAndConditions.section11.content.p3").split('<link>')[0]}
                <a
                  href="/terminos-y-condiciones"
                  className="text-white/90 hover:text-white underline font-medium"
                >
                  {brandHost}/terminos-y-condiciones
                </a>
                {t("termsAndConditions.section11.content.p3").split('</link>')[1]}
              </p>
              <p dangerouslySetInnerHTML={{ __html: t("termsAndConditions.section11.content.p4") }} />
            </div>
          </section>
        </div>

        <div
          className="text-center mt-16 pt-8 border-t border-white/10 animate-fade-in"
          style={{ animationDelay: "0.65s" }}
        >
          <button
            onClick={handleBack}
            className="inline-block text-xs uppercase tracking-[0.25em] text-white/50 hover:text-white/80 transition-colors focus:outline-none focus:ring-2 focus:ring-white/20 rounded px-4 py-2 cursor-pointer"
          >
            ←{" "}
            {canGoBack
              ? t("termsAndConditions.navigation.goBack")
              : t("termsAndConditions.navigation.backToHome")}
          </button>
        </div>

        <footer
          className="mt-12 text-center animate-fade-in"
          style={{ animationDelay: "0.7s" }}
        >
          <p className="text-xs text-white/40 mb-2">
            {t("termsAndConditions.footer.copyright")}
          </p>
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/30">
            {`@${BRAND_HANDLE}`}
          </p>
        </footer>
      </main>
    </div>
  );
};

export default TerminosYCondiciones;
